import React, { useEffect, useState, CSSProperties, useRef } from 'react';
import { TError, TFetchStatus } from 'models';
import {
  SearchWrapper,
  Search,
  CloseIcon,
  SpinnerStyled,
  Drop,
  InputStyled,
  ResultsItem,
  ResultsIcon,
  ResultsItemData,
  ResultsItemDataTitle,
  ResultsItemErrorHeaderTitle,
} from './styles';
import { preflightAPICall, processAPIError, translate } from 'utils';
import axios, { AxiosError } from 'axios';
import { API_BROKER_LIST_URL } from '../../Pages/Broker';

let CancelToken = axios.CancelToken;
let cancelTokenSource = CancelToken.source();

const SEARCH_DELAY = 300;
const MIN_VALUE_LENGTH_TO_START_SEARCH = 3;
const PAGE_SIZE = 10;
let searchTimeout: ReturnType<typeof setTimeout>;

interface props {
  name: string,
  value?: string,
  required?: boolean,
  label?: string,
  style?: CSSProperties,
  capitalized?: boolean,
  handleChange: (name: string, value: string) => void,
  handleSubmit: (item: Record<string, any>) => void,
  validationErrors: string[],
  className?: string,
  disabled?: boolean,
  placeholder?: string,
}

const SearchInput = ({
  name,
  value = '',
  required,
  label,
  style,
  capitalized,
  handleChange,
  handleSubmit,
  validationErrors,
  className,
  disabled,
  placeholder,
}: props) => {
  const [fetchStatus, updateFetchStatus] = useState<TFetchStatus>(null);
  const [errors, updateErrors] = useState<TError[]>([]);
  const [results, updateResults] = useState<Record<string, any>[]>([]);
  const [isDropShown, updateIsDropShown] = useState(false);
  const [isSearchable, updateIsSearchable] = useState(false);
  const searchWrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value.length >= MIN_VALUE_LENGTH_TO_START_SEARCH && isSearchable) {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        callSearch();
      }, SEARCH_DELAY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    window.addEventListener('click', closeDrop);

    return () => {
      window.removeEventListener('click', closeDrop);
    };
  });

  const closeDrop = (event: MouseEvent) => {
    if (searchWrapper) {
      let node = event.target as Element;
      // eslint-disable-next-line eqeqeq
      while (node != null && node.tagName != 'HTML') {
        if (node === searchWrapper.current) {
          return true;
        }
        node = node.parentNode as Element;
      }
      updateIsSearchable(false);
      updateIsDropShown(false);
    }
  };

  const onAPIError = (error: AxiosError) => {
    if (axios.isCancel(error)) {
      // Request canceled
      updateFetchStatus(null);
    } else {
      console.error(error);
      updateErrors(processAPIError(error, true) as TError[]);
      updateFetchStatus('failed');
    }
  };

  const callSearch = () => {
    updateFetchStatus('loading');
    preflightAPICall(() => {
      axios.get(
        `${API_BROKER_LIST_URL}?search=${value}&page_size=${PAGE_SIZE}`, {
          cancelToken: cancelTokenSource ? cancelTokenSource.token : undefined,
        })
        .then(response => {
          const data = response.data;
          updateResults(data.results || []);
          updateFetchStatus('success');

          callSearchCallback({ results: data.results || results, onSubmit });

        })
        .catch(onAPIError);
    });

  };

  const onSubmit = (item: Record<string, any>) => {
    updateIsSearchable(false);
    updateFetchStatus(null);
    // updateResults([]);
    handleSubmit(item);
  };

  const onChange = (name: string, value: string) => {
    if (errors.length) {
      updateErrors([]);
    }

    // if (results.length) {
    //   updateResults([]);
    // }

    if (fetchStatus === 'loading') {
      cancelTokenSource.cancel('Operation canceled by the user.');
      CancelToken = axios.CancelToken;
      cancelTokenSource = CancelToken.source();
    }

    if (fetchStatus) {
      updateFetchStatus(null);
    }

    handleChange(name, value);
  };

  const onFocused = () => {
    updateIsSearchable(true);
    updateIsDropShown(true);
  };

  return (
    <SearchWrapper style={style} className={className} ref={searchWrapper}>
      <Search>
        <InputStyled
          placeholder={placeholder}
          onFocused={onFocused}
          name={name}
          required={required}
          label={label}
          value={results.find(item => item.id === value)?.name || value}
          capitalized={capitalized}
          handleChange={(name, value) => onChange(name as string, value as string)}
          iconAdditional="search_input"
          error={validationErrors?.length ? validationErrors.join('\n') : undefined}
          disabled={disabled}
        />
        {(value?.length && fetchStatus !== 'loading') ? <CloseIcon
          onClick={() => onChange(name, '')}
          icon="close"
        /> : null}
        {fetchStatus === 'loading' && <SpinnerStyled />}
      </Search>
      {((fetchStatus === 'success' && results?.length) || errors?.length) ? (
        <Drop isDropShown={isDropShown}>
          {processResults(results, errors, onSubmit)}
        </Drop>
      ) : null}
    </SearchWrapper>
  );

};

const processResults = (results: Record<string, any>[], errors: TError[], onSubmit: (item: Record<string, any>) => void) => {
  if (!!errors.length) {
    return errors.map((item) => (
      <ResultsItem key={item.code}>
        <ResultsIcon icon="exclamation_mark" />
        <ResultsItemData>
          <ResultsItemErrorHeaderTitle>
            {item.code}
          </ResultsItemErrorHeaderTitle>
          <ResultsItemDataTitle>
            {item.message}
          </ResultsItemDataTitle>
        </ResultsItemData>
      </ResultsItem>
    ));
  }
  if (!results || results.length === 0) {
    return (
      <ResultsItem noData={true}>
        <ResultsIcon icon="placeholder" />
        <ResultsItemData>
          <ResultsItemErrorHeaderTitle>
            {translate({ key: 'common.no_results' })}
          </ResultsItemErrorHeaderTitle>
        </ResultsItemData>
      </ResultsItem>
    );
  }
  return results.map((item) => (
    <ResultsItem onClick={() => onSubmit(item)} key={item.vin}>
      {renderResultsItemData(item)}
    </ResultsItem>
  ));
};

const renderResultsItemData = (item: Record<string, any>) => (
  <ResultsItemData>
    <ResultsItemDataTitle>
      {item.name}
    </ResultsItemDataTitle>
  </ResultsItemData>
);

const callSearchCallback = (props: Record<string, any>) => {
  const { results, onSubmit } = props;
  if (results.length === 1) {
    onSubmit(results[0]);
  }
};

export default SearchInput;
